import { useEffect, useRef } from "react";
import { AttachmentMeta } from "@/indexedDB/types";
import {
  processFileAttachment,
  processMediaAttachment,
} from "@/middleware/attachments";
import { useMediaAutoDownloadState } from "@/zustand/MediaAutoDownloadState";
import { AutoDownloadKind, shouldAutoDownload } from "./autoDownloadPolicy";
import { runAutoDownload } from "./autoDownloadQueue";

type UseAutoDownloadOptions = {
  chatId: string;
  messageId: string;
  timestamp: string;
  attachment?: AttachmentMeta;
  kind: AutoDownloadKind;
};

/**
 * When the matching auto-download preference is enabled, warm the local cache
 * with an attachment's full content as its bubble renders — so opening it is
 * instant. Cache-warming only (fire-and-forget, aborted on unmount); the
 * displayed item still creates its own object URL on demand. Default settings
 * are all-off, so this is a no-op unless the user opts in.
 *
 * All warms run through a shared concurrency cap (`runAutoDownload`) so a
 * media-heavy chat can't fire dozens of fetch+decrypt operations at once.
 */
export default function useAutoDownload({
  chatId,
  messageId,
  timestamp,
  attachment,
  kind,
}: UseAutoDownloadOptions) {
  const autoDownloadPhotos = useMediaAutoDownloadState(
    (s) => s.autoDownloadPhotos,
  );
  const autoDownloadVideos = useMediaAutoDownloadState(
    (s) => s.autoDownloadVideos,
  );
  const autoDownloadFiles = useMediaAutoDownloadState(
    (s) => s.autoDownloadFiles,
  );

  // Keep the churning per-message values reachable from the effect WITHOUT
  // making them deps. A live-query message re-emits a NEW `attachment` object
  // (+ messageId) on every status transition (PENDING → SENT → DELIVERED),
  // though the media is byte-identical; keying the effect on the object ref
  // re-ran it — aborting and re-queueing the warm — on every transition.
  // See useMediaAttachment for the same pattern.
  const latest = useRef({ chatId, messageId, timestamp, attachment });
  latest.current = { chatId, messageId, timestamp, attachment };

  // The STABLE identity + the only fields shouldAutoDownload reads (fileSize
  // cap, large-video exclusion). The effect runs once per real attachment, not
  // on status re-renders.
  const attachmentId = attachment?.attachmentId;
  const fileSize = attachment?.fileSize;
  const isLargeAttachment = attachment?.isLargeAttachment;

  useEffect(() => {
    const att = latest.current.attachment;
    if (!att) {
      return;
    }
    const allowed = shouldAutoDownload(kind, att, {
      autoDownloadPhotos,
      autoDownloadVideos,
      autoDownloadFiles,
    });
    if (!allowed) {
      return;
    }
    const controller = new AbortController();
    // Queue behind the concurrency cap. If the bubble unmounts while still
    // queued, the signal is already aborted, so the task bails immediately when
    // its slot opens (processMediaAttachment/processFileAttachment check it).
    runAutoDownload(() => {
      const {
        chatId: cId,
        messageId: mId,
        timestamp: ts,
        attachment: meta,
      } = latest.current;
      if (controller.signal.aborted || !meta) {
        return Promise.resolve(null);
      }
      return kind === "file"
        ? processFileAttachment({
            chatId: cId,
            messageId: mId,
            timestamp: ts,
            attachmentMeta: meta,
            signal: controller.signal,
          })
        : processMediaAttachment({
            chatId: cId,
            messageId: mId,
            messageTimestamp: ts,
            attachmentMeta: meta,
            getThumbnail: false,
            signal: controller.signal,
          });
    }).catch(() => {
      /* best-effort cache warming */
    });
    return () => controller.abort();
  }, [
    attachmentId,
    fileSize,
    isLargeAttachment,
    kind,
    autoDownloadPhotos,
    autoDownloadVideos,
    autoDownloadFiles,
  ]);
}
